import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { errorMessage, listStates, saveRegionSettings } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { Badge, Button, Card, CardBody, EmptyState, ErrorState, InlineAlert, Skeleton, Tabs, useToast } from '../../ui';
import { RegionsListView } from './regions/RegionsListView';
import { useCityCache } from './regions/useCityCache';
import type { GeoState, RegionSelection } from '../../types';
import { describeSelection, isEmptySelection, regionFromSaved, regionToPayload, sameSelection } from './regionsModel';
import { useWizard } from './WizardContext';

// The map carries d3 + shape data, so it is only downloaded when this step is opened for India.
const RegionsMapView = React.lazy(() => import('./regions/RegionsMapView'));

const VIEWS = [{ key: 'map', label: 'Map' }, { key: 'list', label: 'List' }];
// The state records returned by the geo endpoint carry country_code; the map only exists for India.
const isIndia = (states: GeoState[]) => states.length > 0 && states.every((s) => s.country_code === 'IN');

export function StepRegions() {
  const { project, targetGroup, reload, goStep, setDirty } = useWizard();
  const { notify } = useToast();
  const countryId = targetGroup.basicSetting?.country_id ?? '';
  const states = useAsyncData<GeoState[]>((signal) => listStates(countryId, { signal }), [countryId]);
  const cities = useCityCache(countryId);

  const [selection, setSelection] = useState<RegionSelection>(() => regionFromSaved(targetGroup.region));
  const [view, setView] = useState('map');
  const [formError, setFormError] = useState('');
  const [submitting, run] = useSubmitGuard();

  // Unsaved when the picked regions differ from what the server holds; the wizard asks before leaving then.
  const dirty = useMemo(() => !sameSelection(selection, regionFromSaved(targetGroup.region)), [selection, targetGroup.region]);
  useEffect(() => { setDirty(dirty); return () => setDirty(false); }, [dirty, setDirty]);

  const stateList = states.data || [];
  const mapAvailable = isIndia(stateList);
  const nothingToSave = isEmptySelection(selection) && !targetGroup.region;
  const showMap = mapAvailable && view === 'map';
  const viewTabs = mapAvailable ? <Tabs tabs={VIEWS} value={view} onChange={setView} label="Region picker view" /> : null;

  const save = () => {
    setFormError('');
    if (nothingToSave) { goStep('profiling', { force: true }); return undefined; }
    return run(async () => {
      try {
        await saveRegionSettings(project.id, targetGroup.id, regionToPayload(selection));
        await reload();
        notify('Regions saved', { tone: 'ok' });
        goStep('profiling', { force: true }); // saved: nothing left to lose
      } catch (err) {
        setFormError(errorMessage(err));
      }
    });
  };

  // Everything except the map view lives in one card with a header; the map view brings its own two cards.
  const singleCard = (content: React.ReactNode) => (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Regions</h2>
            <p className="mt-1 text-sm text-muted">Optionally limit this target group to specific states and cities. Leave empty to reach the whole country.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {viewTabs}
            <Badge tone={isEmptySelection(selection) ? 'neutral' : 'brand'}>{describeSelection(selection)}</Badge>
          </div>
        </div>
        <div className="mt-5">{content}</div>
      </CardBody>
    </Card>
  );

  let body;
  if (states.loading && !states.data) {
    body = singleCard(<div className="space-y-2" aria-busy="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-11 w-full" />)}</div>);
  } else if (states.error) {
    body = singleCard(<ErrorState title="Could not load states" message={errorMessage(states.error)} onRetry={states.reload} />);
  } else if (stateList.length === 0) {
    body = singleCard(<EmptyState title="No regions available" description="There is no region data for the selected country." />);
  } else if (showMap) {
    body = (
      <Suspense fallback={singleCard(<div aria-busy="true" aria-label="Loading map"><Skeleton className="h-96 w-full" /></div>)}>
        <RegionsMapView states={stateList} selection={selection} onChange={setSelection} cities={cities} viewTabs={viewTabs} />
      </Suspense>
    );
  } else {
    body = singleCard(<RegionsListView states={stateList} selection={selection} onChange={setSelection} cities={cities} />);
  }

  return (
    <div>
      {body}
      {formError && <div className="mt-4"><InlineAlert>{formError}</InlineAlert></div>}
      <div className="mt-4 flex justify-between">
        <Button variant="secondary" onClick={() => goStep('basics')}>← Back</Button>
        <Button onClick={save} loading={submitting}>{nothingToSave ? 'Skip regions →' : 'Save & continue →'}</Button>
      </div>
    </div>
  );
}
